class UgcVideoCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.jp-ugc__track');
    this.originalCards = Array.from(this.querySelectorAll('.jp-ugc__card'));
    this.realCount = this.originalCards.length;

    // Seconds → ms; read from data attribute set by Liquid schema
    this.slideInterval = (parseInt(this.dataset.slideInterval, 10) || 7) * 1000;

    if (!this.track || this.realCount === 0) return;

    // ── Clone enough full sets on each side to fill the screen ──────────────
    // At ~21rem (≈336px) per card + 2.5rem gap (≈40px) = ~376px per slot.
    // A 1440px viewport needs ~4 cards per side; we buffer to 8 minimum slots,
    // and require at least 3 full repetitions so the loop never runs dry.
    this.sideReps = Math.max(Math.ceil(8 / this.realCount), 3);

    // Prepend sideReps full sets.
    // Reversing then inserting-before-firstChild produces the correct card order.
    for (let r = 0; r < this.sideReps; r++) {
      [...this.originalCards].reverse().forEach(card => {
        this.track.insertBefore(this.cloneCard(card), this.track.firstChild);
      });
    }
    // Append sideReps full sets.
    for (let r = 0; r < this.sideReps; r++) {
      this.originalCards.forEach(card => this.track.appendChild(this.cloneCard(card)));
    }

    // Re-query so this.cards includes originals + all clones in DOM order.
    this.cards = Array.from(this.track.querySelectorAll('.jp-ugc__card'));

    // Start on the middle card of the original set.
    this.activeIndex = this.sideReps * this.realCount + Math.floor(this.realCount / 2);

    this.isDragging = false;
    this.dragMoved = false;
    this.startX = 0;
    this.startScrollLeft = 0;

    // ── Wire mute buttons (originals + clones) ───────────────────────────────
    this.cards.forEach(card => {
      const btn = card.querySelector('.jp-ugc__mute-btn');
      const video = card.querySelector('video');
      if (btn && video) {
        btn.addEventListener('click', e => {
          e.stopPropagation();
          this.toggleMute(video, btn);
        });
      }
    });

    // ── Clicking an inactive card brings it to the centre ────────────────────
    this.cards.forEach((card, i) => {
      card.addEventListener('click', e => {
        if (this.dragMoved) return;
        if (i === this.activeIndex) return;
        if (e.target.closest('a, .jp-ugc__mute-btn')) return;
        this.goTo(i, true);
        this.resetTimer();
      });
    });

    // ── Initial render ────────────────────────────────────────────────────────
    this.updateActive();
    requestAnimationFrame(() => {
      this.scrollToActive(false);
      // Re-centre after the 0.4s CSS width transition has settled.
      setTimeout(() => this.scrollToActive(false), 420);
    });

    // ── Drag (mouse) ──────────────────────────────────────────────────────────
    this.track.addEventListener('mousedown', this.onMouseDown.bind(this));
    window.addEventListener('mousemove', this.onMouseMove.bind(this));
    window.addEventListener('mouseup', this.onMouseUp.bind(this));
    this.track.addEventListener('dragstart', e => e.preventDefault());

    // ── Swipe (touch) ─────────────────────────────────────────────────────────
    this.track.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
    this.track.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: true });

    // ── Pause auto-advance while the user hovers ──────────────────────────────
    this.addEventListener('mouseenter', () => this.clearTimer());
    this.addEventListener('mouseleave', () => this.startTimer());

    window.addEventListener('resize', () => this.scrollToActive(false));

    this.startTimer();
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  cloneCard(card) {
    const clone = card.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));
    return clone;
  }

  goTo(index, smooth) {
    this.activeIndex = index;
    this.updateActive();
    this.scrollToActive(smooth);
    setTimeout(() => this.normalizeIndex(), 450);
  }

  updateActive() {
    this.cards.forEach((card, i) => {
      const isActive = i === this.activeIndex;
      card.classList.toggle('is-active', isActive);

      const video = card.querySelector('video');
      const muteBtn = card.querySelector('.jp-ugc__mute-btn');

      if (!video) return;

      if (isActive) {
        video.play().catch(() => {});
      } else {
        video.pause();
        // Reset audio state so every card starts muted when it becomes active.
        video.muted = true;
        if (muteBtn) muteBtn.classList.remove('is-unmuted');
      }
    });
  }

  // Uses getBoundingClientRect so the result is correct regardless of
  // offsetParent and works both on the live site and in the Shopify editor
  // (which has a sidebar that shifts card.offsetLeft without shifting scrollLeft).
  scrollToActive(smooth) {
    const card = this.cards[this.activeIndex];
    if (!card) return;
    this.track.style.scrollBehavior = smooth ? 'smooth' : 'auto';
    const trackRect = this.track.getBoundingClientRect();
    const cardRect  = card.getBoundingClientRect();
    const posInContent = (cardRect.left - trackRect.left) + this.track.scrollLeft;
    this.track.scrollLeft = posInContent - this.track.clientWidth / 2 + card.offsetWidth / 2;
  }

  // Keep activeIndex inside the original-set window so the loop is seamless.
  normalizeIndex() {
    const lo = this.sideReps * this.realCount;
    const hi = lo + this.realCount;
    if (this.activeIndex < lo) {
      this.activeIndex += this.realCount;
      this.updateActive();
      this.scrollToActive(false);
    } else if (this.activeIndex >= hi) {
      this.activeIndex -= this.realCount;
      this.updateActive();
      this.scrollToActive(false);
    }
  }

  snapToNearest() {
    const trackRect    = this.track.getBoundingClientRect();
    const viewCenter   = trackRect.left + this.track.clientWidth / 2;
    let closest = this.activeIndex;
    let minDist = Infinity;
    this.cards.forEach((card, i) => {
      const rect = card.getBoundingClientRect();
      const dist = Math.abs((rect.left + card.offsetWidth / 2) - viewCenter);
      if (dist < minDist) { minDist = dist; closest = i; }
    });
    this.goTo(closest, true);
    this.resetTimer();
  }

  // ── Auto-advance timer ─────────────────────────────────────────────────────

  startTimer() {
    this.clearTimer();
    this.autoTimer = setTimeout(() => {
      this.goTo(this.activeIndex + 1, true);
      this.startTimer();
    }, this.slideInterval);
  }

  clearTimer() {
    if (this.autoTimer) clearTimeout(this.autoTimer);
  }

  resetTimer() {
    this.startTimer();
  }

  // ── Mute toggle ───────────────────────────────────────────────────────────

  toggleMute(video, btn) {
    // Unmuting: mute every other card's video first (one audio source at a time).
    if (video.muted) {
      this.cards.forEach(card => {
        const v = card.querySelector('video');
        const b = card.querySelector('.jp-ugc__mute-btn');
        if (v && v !== video) {
          v.muted = true;
          if (b) b.classList.remove('is-unmuted');
        }
      });
    }
    video.muted = !video.muted;
    btn.classList.toggle('is-unmuted', !video.muted);
  }

  // ── Mouse drag ────────────────────────────────────────────────────────────

  onMouseDown(e) {
    if (e.button !== 0) return;
    this.isDragging = true;
    this.dragMoved = false;
    this.startX = e.clientX;
    this.startScrollLeft = this.track.scrollLeft;
    this.track.classList.add('is-dragging');
    this.track.style.scrollBehavior = 'auto';
    this.clearTimer();
  }

  onMouseMove(e) {
    if (!this.isDragging) return;
    const delta = e.clientX - this.startX;
    if (Math.abs(delta) > 5) this.dragMoved = true;
    this.track.scrollLeft = this.startScrollLeft - delta;
  }

  onMouseUp() {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.track.classList.remove('is-dragging');
    if (this.dragMoved) this.snapToNearest();
  }

  // ── Touch swipe ───────────────────────────────────────────────────────────

  onTouchStart(e) {
    this.touchStartX = e.touches[0].clientX;
    this.clearTimer();
  }

  onTouchEnd(e) {
    const delta = e.changedTouches[0].clientX - this.touchStartX;
    if (Math.abs(delta) > 40) {
      this.goTo(this.activeIndex + (delta < 0 ? 1 : -1), true);
      this.resetTimer();
    } else {
      this.snapToNearest();
    }
  }
}

customElements.define('ugc-video-carousel', UgcVideoCarousel);
